import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, X, Loader2, PenLine, Save, Mail, Calendar, Bell,
  Trophy, TrendingDown, TrendingUp, Minus, ArrowRight, Users, Clock, MapPin,
  History, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  API, DAYS_OF_WEEK, TIMEZONES, COACHING_TYPES,
} from './domainUtils';
import { PipelineHealth } from './overview/PipelineHealth';
import { formatUtcHourLocal } from '../../../lib/timezone';
import { BrandingSection } from './BrandingSection';

// ── Company details ──────────────────────────────────────────────────────────
const CompanyDetails = ({ domain }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
    <h3 className="text-white font-semibold mb-3">Company Details</h3>
    <dl className="space-y-2 text-sm">
      {[
        ['ABN', domain?.company?.abn],
        ['Address', domain?.company?.address],
        ['Contact', domain?.company?.contact_name],
        ['Phone', domain?.company?.contact_phone],
        ['Email', domain?.company?.contact_email],
        ['Website', domain?.company?.website],
        ['Last Sync', domain?.last_sync ? new Date(domain.last_sync).toLocaleString() : null],
      ].map(([k, v]) => v ? (
        <div key={k} className="flex gap-2">
          <dt className="text-white/30 w-20 shrink-0">{k}</dt>
          <dd className="text-white/70 break-all">{v}</dd>
        </div>
      ) : null)}
    </dl>
  </div>
);

// ── Extensions section ───────────────────────────────────────────────────────
const ExtensionsSection = ({ domain, domainId, token, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [siteDraft, setSiteDraft] = useState({});
  const [newExt, setNewExt] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      // Persist the two orthogonal maps in parallel — classifications on the
      // domain object itself, site labels via the dedicated endpoint added
      // for the revivable-deal same-site auto-assign rule.
      const cleanSites = Object.fromEntries(
        Object.entries(siteDraft).filter(([, v]) => v && v.trim()),
      );
      await Promise.all([
        axios.put(`${API}/admin/domains/${domainId}`, {
          extension_classifications: draft,
          extensions_monitored: Object.keys(draft),
        }, { headers: { Authorization: `Bearer ${token}` } }),
        axios.put(`${API}/admin/domains/${domainId}/extension-sites`, cleanSites, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      toast.success('Extension settings saved');
      setEditing(false);
      onRefresh();
    } catch (err) {
      toast.error('Failed to save extensions');
      console.error('Save extensions error:', err);
    } finally {
      setSaving(false);
    }
  };

  const addExt = () => {
    const v = newExt.trim();
    if (!v) return;
    setDraft(d => ({ ...d, [v]: '' }));
    setSiteDraft(s => ({ ...s, [v]: s[v] || '' }));
    setNewExt('');
  };

  const sortedDraftEntries = useMemo(
    () => Object.entries(draft).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })),
    [draft],
  );

  if (editing) return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          value={newExt}
          onChange={e => setNewExt(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addExt(); }}
          placeholder="Add extension (e.g. 9919)"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
          data-testid="new-extension-input"
        />
        <button onClick={addExt} className="px-3 py-1.5 bg-teal-500 hover:bg-teal-400 text-white rounded-lg text-xs">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {sortedDraftEntries.map(([ext, coachType]) => (
          <div key={ext} className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-1.5">
            <span className="text-sm font-mono text-teal-300 w-16 shrink-0">{ext}</span>
            <select
              value={coachType || ''}
              onChange={e => setDraft(d => ({ ...d, [ext]: e.target.value }))}
              className="flex-1 bg-[#0A0F1C] border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-teal-500/50"
              data-testid={`ext-type-select-${ext}`}
            >
              <option value="">Use domain default</option>
              {COACHING_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
            </select>
            <input
              value={siteDraft[ext] || ''}
              onChange={e => setSiteDraft(s => ({ ...s, [ext]: e.target.value }))}
              placeholder="Site (e.g. Chermside)"
              className="w-40 bg-[#0A0F1C] border border-white/10 rounded px-2 py-1 text-xs text-white placeholder-white/20 focus:outline-none focus:border-teal-500/50"
              data-testid={`ext-site-input-${ext}`}
              title="Physical site — used to keep the Revivable Deal auto-assignment on the same site as the original rep"
            />
            <button
              onClick={() => {
                setDraft(d => { const n = { ...d }; delete n[ext]; return n; });
                setSiteDraft(s => { const n = { ...s }; delete n[ext]; return n; });
              }}
              className="text-white/20 hover:text-red-400 transition-colors"
              data-testid={`remove-ext-${ext}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        {Object.keys(draft).length === 0 && (
          <p className="text-white/20 text-xs text-center py-2">No extensions — add one above</p>
        )}
      </div>
      <p className="text-[11px] text-white/40 italic">
        Site label is optional. When set, the Revivable Deal queue will restrict its auto-assignment
        to reps on the same site.
      </p>
      <div className="flex gap-2 pt-1">
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white text-sm font-medium rounded-lg disabled:opacity-50"
          data-testid="save-extensions-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save
        </button>
        <button onClick={() => setEditing(false)} className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 text-sm rounded-lg">Cancel</button>
      </div>
    </div>
  );

  const exts = domain?.extensions_monitored || [];
  return (
    <>
      <div className="flex items-center justify-end mb-2">
        <button
          onClick={() => {
            setDraft({ ...(domain?.extension_classifications || {}) });
            setSiteDraft({ ...(domain?.extension_sites || {}) });
            setEditing(true);
          }}
          className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1"
          data-testid="edit-extensions-btn"
        >
          <PenLine className="w-3 h-3" />Edit
        </button>
      </div>
      {exts.length > 0 ? (
        <div className="space-y-2">
          {exts.map(e => {
            const coachType = domain.extension_classifications?.[e];
            const site = domain.extension_sites?.[e];
            const label = coachType
              ? COACHING_TYPES.find(ct => ct.value === coachType)?.label || coachType.replace(/_/g, ' ')
              : domain.industry?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            return (
              <div key={e} className="flex items-center justify-between gap-2">
                <span className="text-sm font-mono text-teal-300 bg-teal-500/10 border border-teal-500/20 px-2 py-0.5 rounded shrink-0">{e}</span>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  {site && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {site}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${coachType ? 'bg-violet-500/20 text-violet-300 border border-violet-500/20' : 'bg-white/5 text-white/30 border border-white/10'}`}>
                    {label}
                    {!coachType && <span className="ml-1 text-white/20">(default)</span>}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : <p className="text-white/20 text-sm">No extensions set — click Edit to add</p>}
    </>
  );
};

// ── Notification emails section ──────────────────────────────────────────────
const NotificationEmailsSection = ({ domain, domainId, token, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const cleaned = draft.filter(e => e.trim());
    setSaving(true);
    try {
      await axios.put(`${API}/admin/domains/${domainId}`, { notification_emails: cleaned }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Notification emails updated');
      setEditing(false);
      onRefresh();
    } catch (err) {
      toast.error('Failed to save emails');
      console.error('Save emails error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (editing) return (
    <div className="space-y-2">
      {draft.map((email, i) => (
        <div key={`email-draft-${i}`} className="flex items-center gap-2">
          <input
            type="email"
            value={email}
            onChange={e => setDraft(d => d.map((x, j) => j === i ? e.target.value : x))}
            placeholder="email@domain.com"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
            data-testid={`email-draft-${i}`}
          />
          <button
            onClick={() => setDraft(d => d.filter((_, j) => j !== i))}
            className="text-white/20 hover:text-red-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button onClick={() => setDraft(d => [...d, ''])} className="flex items-center gap-1 text-teal-400 text-xs hover:text-teal-300">
        <Plus className="w-3.5 h-3.5" /> Add Email
      </button>
      <div className="flex gap-2 pt-1">
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-500 hover:bg-teal-400 text-white text-xs font-medium rounded-lg disabled:opacity-50"
          data-testid="save-emails-btn"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}Save
        </button>
        <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs text-white/40 hover:text-white/70">Cancel</button>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-white font-semibold">Notification Emails</h3>
        <button
          onClick={() => { setDraft([...(domain?.notification_emails || []), '']); setEditing(true); }}
          className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1"
          data-testid="edit-emails-btn"
        >
          <PenLine className="w-3 h-3" /> Edit
        </button>
      </div>
      {domain?.notification_emails?.length > 0 ? (
        <div className="space-y-1">
          {domain.notification_emails.map(e => (
            <p key={e} className="text-sm text-white/50">{e}</p>
          ))}
        </div>
      ) : <p className="text-white/20 text-sm">No emails configured — click Edit to add</p>}
    </>
  );
};

// ── Email routing section ────────────────────────────────────────────────────
const EmailRoutingSection = ({ routing, domainId, token, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/domains/${domainId}/email-routing-overrides`,
        { overrides: draft },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Email routing overrides saved');
      setEditing(false);
      onRefresh();
    } catch (err) {
      toast.error('Failed to save routing overrides');
      console.error('Save routing error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!routing?.routing?.length) return null;

  return (
    <div className="mt-5">
      <h3 className="text-white font-semibold mb-1 flex items-center justify-between">
        <span className="flex items-center gap-2"><Mail className="w-4 h-4 text-emerald-400" /> Email Routing by Category</span>
        {!editing && (
          <button
            onClick={() => {
              const d = {};
              routing.routing.forEach(r => {
                d[r.category] = [...(r.custom_emails?.length ? r.custom_emails : [''])];
              });
              setDraft(d);
              setEditing(true);
            }}
            className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1"
            data-testid="edit-routing-btn"
          >
            <PenLine className="w-3 h-3" /> Edit
          </button>
        )}
      </h3>
      <p className="text-white/30 text-xs mb-3">
        Set custom emails per category — overrides the auto-generated plus addresses
      </p>

      {editing ? (
        <div className="space-y-4">
          {routing.routing.map(r => (
            <div key={r.category} className="bg-white/5 rounded-xl p-3 border border-white/10">
              <p className="text-teal-300 text-xs font-semibold mb-2">{r.label}</p>
              <p className="text-white/20 text-xs mb-2">Auto: {r.plus_addresses.join(', ') || 'none'}</p>
              <div className="space-y-1.5">
                {(draft[r.category] || ['']).map((em, i) => (
                  <div key={`${r.category}-${i}`} className="flex items-center gap-2">
                    <input
                      type="email"
                      value={em}
                      onChange={e => setDraft(d => ({
                        ...d,
                        [r.category]: (d[r.category] || []).map((x, j) => j === i ? e.target.value : x),
                      }))}
                      placeholder={`Custom email for ${r.label}...`}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                    />
                    <button
                      onClick={() => setDraft(d => ({ ...d, [r.category]: (d[r.category] || []).filter((_, j) => j !== i) }))}
                      className="text-white/20 hover:text-red-400"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setDraft(d => ({ ...d, [r.category]: [...(d[r.category] || []), ''] }))}
                  className="text-teal-400/60 text-xs hover:text-teal-300 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add email
                </button>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-500 hover:bg-teal-400 text-white text-xs font-medium rounded-lg disabled:opacity-50"
              data-testid="save-routing-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs text-white/40 hover:text-white/70">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          {routing.routing.map(r => (
            <div key={r.category} className="flex items-start justify-between gap-3 bg-white/5 rounded-lg px-3 py-2">
              <span className="text-xs font-medium text-teal-300 shrink-0">{r.label}</span>
              <div className="text-right">
                {(r.custom_emails?.length ? r.custom_emails : r.plus_addresses).map(addr => (
                  <p key={addr} className={`text-xs font-mono ${r.custom_emails?.length ? 'text-emerald-300' : 'text-white/40'}`}>{addr}</p>
                ))}
                {r.custom_emails?.length > 0 && <span className="text-xs text-emerald-500/60">(custom)</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Send coaching email button ───────────────────────────────────────────────
const SendCoachingButton = ({ onSend, sending, testId }) => (
  <button
    onClick={onSend}
    disabled={sending}
    data-testid={testId}
    className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
  >
    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
    {sending ? 'Generating & Sending...' : 'Send Weekly Coaching Report Now'}
  </button>
);

const tzOffsetMin = (tz) => {
  const now = new Date();
  return (new Date(now.toLocaleString('en-US', { timeZone: tz })) - now) / 60000;
};

// ── Weekly coaching schedule ─────────────────────────────────────────────────
const ScheduleSection = ({ domainId, token, initialSchedule }) => {
  const [schedule, setSchedule] = useState(initialSchedule);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSchedule(initialSchedule); }, [initialSchedule]);

  const aestHint = useMemo(() => {
    try {
      const diff = tzOffsetMin('Australia/Brisbane') - tzOffsetMin(schedule.timezone || 'UTC');
      let total = (schedule.day_of_week || 0) * 1440 + (schedule.hour || 0) * 60 + (schedule.minute || 0) + diff;
      total = ((total % 10080) + 10080) % 10080;
      const d = Math.floor(total / 1440), h = Math.floor((total % 1440) / 60), m = Math.round(total % 60);
      const hh = h % 12 === 0 ? 12 : h % 12;
      return `${DAYS_OF_WEEK[d]} ${hh}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'} AEST`;
    } catch { return null; }
  }, [schedule]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/domains/${domainId}/coaching-schedule`, schedule,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message);
    } catch (err) {
      toast.error('Failed to save schedule');
      console.error('Save schedule error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 border border-white/10 rounded-xl p-4">
      <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-violet-400" />
        Weekly Coaching Schedule
        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${schedule.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/5 text-white/20'}`}>
          {schedule.enabled ? 'Active' : 'Off'}
        </span>
      </h3>
      <p className="text-white/30 text-xs mb-4">Auto-send the weekly coaching report on a schedule</p>
      <div className="space-y-3">
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <div
            className={`w-10 h-5 rounded-full transition-colors relative ${schedule.enabled ? 'bg-teal-500' : 'bg-white/10'}`}
            onClick={() => setSchedule(s => ({ ...s, enabled: !s.enabled }))}
          >
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${schedule.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
          <span className="text-sm text-white/70">
            {schedule.enabled ? 'Enabled — auto-send active' : 'Disabled — manual send only'}
          </span>
        </label>

        <div className="grid grid-cols-4 gap-2">
          <div>
            <label className="text-white/30 text-xs block mb-1">Day of week</label>
            <select
              value={schedule.day_of_week}
              onChange={e => setSchedule(s => ({ ...s, day_of_week: parseInt(e.target.value, 10) }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-teal-500/50"
              data-testid="schedule-day"
            >
              {DAYS_OF_WEEK.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/30 text-xs block mb-1">Time (hour)</label>
            <select
              value={schedule.hour}
              onChange={e => setSchedule(s => ({ ...s, hour: parseInt(e.target.value, 10) }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-teal-500/50"
              data-testid="schedule-hour"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-white/30 text-xs block mb-1">Minute</label>
            <select
              value={schedule.minute || 0}
              onChange={e => setSchedule(s => ({ ...s, minute: parseInt(e.target.value, 10) }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-teal-500/50"
              data-testid="schedule-minute"
            >
              {[0, 15, 30, 45].map(m => <option key={m} value={m}>:{String(m).padStart(2, '0')}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/30 text-xs block mb-1">Timezone</label>
            <select
              value={schedule.timezone}
              onChange={e => setSchedule(s => ({ ...s, timezone: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-teal-500/50"
              data-testid="schedule-timezone"
            >
              {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz === 'Australia/Brisbane' ? 'Australia/Brisbane (AEST)' : tz}</option>)}
            </select>
          </div>
        </div>

        {aestHint && (
          <p className="text-teal-300/70 text-xs" data-testid="schedule-aest-hint">
            Sends every {aestHint}
          </p>
        )}

        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600/30 hover:bg-violet-600/40 border border-violet-500/30 text-violet-300 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 w-full justify-center"
          data-testid="save-schedule-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
          {saving ? 'Saving...' : `Save Schedule${schedule.enabled ? ` — ${DAYS_OF_WEEK[schedule.day_of_week]}s at ${schedule.hour}:${String(schedule.minute || 0).padStart(2, '0')} ${schedule.timezone}` : ''}`}
        </button>
      </div>
    </div>
  );
};

// ── Team Leaderboard section ─────────────────────────────────────────────────
const getInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const scoreColor = (score) => {
  if (score >= 80) return 'text-emerald-300';
  if (score >= 70) return 'text-teal-300';
  if (score >= 60) return 'text-amber-300';
  return 'text-red-300';
};

const LeaderboardRow = ({ agent, rank, variant, onAgentClick, domainExtensions }) => {
  const isTop = variant === 'top';
  const medal = isTop ? ['🥇', '🥈', '🥉'][rank] : null;
  const ringColor = isTop ? 'from-emerald-400/40 to-teal-500/40' : 'from-amber-500/30 to-red-500/30';
  const agentId = agent.agent_id;
  // Prefer the explicit `extension` field from the API; fall back to legacy
  // logic that compared agent_id against the monitored list.
  const extension = agent.extension
    || ((domainExtensions || []).includes(String(agentId)) ? String(agentId) : null);

  // Trend arrow: server returns `trend` as (second_half_avg - first_half_avg)
  const trend = agent.trend ?? 0;
  let TrendIcon = Minus;
  let trendColor = 'text-white/30';
  let trendTitle = 'No change';
  if (trend >= 1.5) {
    TrendIcon = TrendingUp;
    trendColor = 'text-emerald-400';
    trendTitle = `Improving · +${trend.toFixed(1)} pts`;
  } else if (trend <= -1.5) {
    TrendIcon = TrendingDown;
    trendColor = 'text-rose-400';
    trendTitle = `Declining · ${trend.toFixed(1)} pts`;
  }

  return (
    <button
      type="button"
      onClick={() => onAgentClick && onAgentClick(agentId)}
      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-teal-500/30 transition-all text-left group"
      data-testid={`leaderboard-row-${variant}-${rank}`}
    >
      <div className="shrink-0 w-6 text-center text-xs font-bold text-white/40">
        {medal || `#${rank + 1}`}
      </div>
      <div className={`shrink-0 w-9 h-9 rounded-full bg-gradient-to-br ${ringColor} flex items-center justify-center text-white text-xs font-bold`}>
        {getInitials(agent.agent_name)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate font-medium group-hover:text-teal-300 transition-colors">
          {agent.agent_name || `Ext ${agentId}`}
        </p>
        <p className="text-xs text-white/40">
          {extension ? `ext ${extension}` : agentId} · {agent.total_calls} call{agent.total_calls === 1 ? '' : 's'}
        </p>
      </div>
      <div className="shrink-0 flex items-center gap-1.5" title={trendTitle}>
        <TrendIcon className={`w-3.5 h-3.5 ${trendColor}`} data-testid={`leaderboard-trend-${variant}-${rank}`} />
        <div className="text-right">
          <p className={`text-base font-bold tabular-nums ${scoreColor(agent.avg_score)}`}>
            {agent.avg_score}
          </p>
          <p className="text-[10px] text-white/30 uppercase tracking-wide">avg</p>
        </div>
      </div>
    </button>
  );
};

const LeaderboardSection = ({ domainId, token, domain, onAgentClick, onViewAll }) => {
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/analytics/team?domain_id=${domainId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setLeaderboard(r.data?.leaderboard || []);
      } catch (err) {
        console.error('Leaderboard load error:', err);
        if (!cancelled) setLeaderboard([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [domainId, token]);

  const { top3, bottom3 } = useMemo(() => {
    const sorted = [...leaderboard].sort((a, b) => b.avg_score - a.avg_score);
    const t3 = sorted.slice(0, 3);
    // Bottom 3: lowest scores, but exclude anyone already in top3 (handles <=6 agents)
    const topIds = new Set(t3.map(a => a.agent_id));
    const rest = sorted.filter(a => !topIds.has(a.agent_id));
    const b3 = rest.slice(-3).reverse(); // lowest first
    return { top3: t3, bottom3: b3 };
  }, [leaderboard]);

  const domainExtensions = domain?.extensions_monitored || [];

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-center justify-center h-40">
        <Loader2 className="w-5 h-5 animate-spin text-teal-400" />
      </div>
    );
  }

  if (leaderboard.length === 0) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-teal-400" />
          <h3 className="text-white font-semibold">Team Leaderboard</h3>
        </div>
        <p className="text-white/30 text-sm">
          No graded calls yet — leaderboard will populate once agents have graded calls.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="leaderboard-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-teal-400" />
          <h3 className="text-white font-semibold">Team Leaderboard</h3>
          <span className="text-xs text-white/30">({leaderboard.length} agents)</span>
        </div>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-xs text-teal-400 hover:text-teal-300 flex items-center gap-1"
            data-testid="leaderboard-view-all-btn"
          >
            Full analytics <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-3.5 h-3.5 text-emerald-400" />
            <h4 className="text-emerald-300 text-xs font-semibold uppercase tracking-wider">Top Performers</h4>
          </div>
          <div className="space-y-2">
            {top3.map((a, i) => (
              <LeaderboardRow
                key={a.agent_id}
                agent={a}
                rank={i}
                variant="top"
                onAgentClick={onAgentClick}
                domainExtensions={domainExtensions}
              />
            ))}
          </div>
        </div>

        {bottom3.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-3.5 h-3.5 text-amber-400" />
              <h4 className="text-amber-300 text-xs font-semibold uppercase tracking-wider">Needs Coaching</h4>
            </div>
            <div className="space-y-2">
              {bottom3.map((a, i) => (
                <LeaderboardRow
                  key={a.agent_id}
                  agent={a}
                  rank={i}
                  variant="bottom"
                  onAgentClick={onAgentClick}
                  domainExtensions={domainExtensions}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Peak-hour shift alert ────────────────────────────────────────────────────
const PeakShiftAlert = ({ domainId, token }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/analytics/volume-heatmap-shift?domain_id=${domainId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { /* silent — non-critical widget */ });
    return () => { cancelled = true; };
  }, [domainId, token]);

  if (!data?.shift_detected) return null;
  const delta = Math.abs(data.shift_hours);
  const dir = data.direction;
  const thisPeak = data.this_peak?.hour;
  const prevPeak = data.prev_peak?.hour;

  return (
    <div
      className="flex items-start gap-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl px-5 py-3"
      data-testid="peak-shift-alert"
    >
      <Clock className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-amber-200 text-sm font-semibold">
          Call volume peaked {delta}h {dir} this week
        </p>
        <p className="text-amber-200/60 text-xs mt-0.5">
          This week's peak: <strong className="text-white">{formatUtcHourLocal(thisPeak)}</strong> ({data.this_peak?.count} calls) ·
          Previous week: <strong className="text-white">{formatUtcHourLocal(prevPeak)}</strong> ({data.prev_peak?.count} calls).
          Consider reviewing staffing for the new peak window.
        </p>
      </div>
    </div>
  );
};

// ── Region label picker (free-text, powers the master regional rollup) ──────
const RegionSection = ({ domain, domainId, token, onRefresh }) => {
  const [draft, setDraft] = useState(domain?.region || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(domain?.region || '');
  }, [domain?.region]);

  const dirty = (domain?.region || '') !== draft.trim();
  const handleSave = async () => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/domains/${domainId}/region`,
        { region: draft.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(draft.trim() ? `Region set to "${draft.trim()}"` : 'Region cleared');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save region');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="region-editor">
      <h3 className="text-white font-semibold mb-3">Region</h3>
      <p className="text-xs text-white/50 mb-3">
        Free-text label — used on the master overview&apos;s regional rollup to group tenants
        (e.g. &quot;Brisbane&quot;, &quot;NSW&quot;, &quot;APAC&quot;). Leave blank for &quot;Unassigned&quot;.
      </p>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Brisbane, NSW, APAC"
          maxLength={60}
          className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-teal-400"
          data-testid="region-input"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || busy}
          className="px-3 py-2 rounded text-xs font-semibold bg-teal-500 text-white disabled:opacity-40"
          data-testid="region-save-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
        </button>
      </div>
    </div>
  );
};


// ── Training Program picker ──────────────────────────────────────────────────
const TRAINING_PROGRAM_OPTIONS = [
  {
    value: 'tfvf',
    label: 'Total Fusion (TFVF)',
    desc: 'Curated 11-module curriculum with quizzes, practice lab, and certification. Best for the Total Fusion tenant.',
  },
  {
    value: 'kb_generic',
    label: 'KB-driven training',
    desc: "Auto-derived from this domain's knowledge bases. Agents read the same criteria their calls are graded on.",
  },
  {
    value: 'none',
    label: 'Off — hide training UI',
    desc: "No training menu, no /training page. Pick this until you're ready to enable one of the other modes.",
  },
];

const TrainingProgramSection = ({ domain, domainId, token, onRefresh }) => {
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const current = domain?.training_program || 'none';

  const refetchHistory = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/admin/domains/${domainId}/audit-log?field=training_program&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setHistory(r.data?.entries || []);
    } catch (e) {
      setHistory([]);
    }
  }, [domainId, token]);

  const handleChange = async (next) => {
    if (next === current) return;
    setBusy(true);
    try {
      await axios.patch(
        `${API}/admin/domains/${domainId}/training-program`,
        { program: next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Training program set to "${next}"`);
      onRefresh?.();
      // If the history disclosure is open, refresh it so the new entry
      // appears immediately without a manual re-expand.
      if (historyOpen) refetchHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update training program');
    } finally {
      setBusy(false);
    }
  };

  const toggleHistory = async () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && history === null) await refetchHistory();
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid="training-program-picker">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-white font-semibold">Training program</h3>
        {busy && <Loader2 className="w-4 h-4 animate-spin text-white/60" />}
      </div>
      <p className="text-xs text-white/50 mb-3">
        Controls which /training UI every user in this domain sees. Master admin only.
      </p>
      <div className="space-y-2">
        {TRAINING_PROGRAM_OPTIONS.map((opt) => {
          const active = current === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleChange(opt.value)}
              disabled={busy || active}
              className={`w-full text-left rounded-lg border p-3 transition-all ${
                active
                  ? 'border-emerald-400 bg-emerald-500/10'
                  : 'border-white/10 bg-white/5 hover:bg-white/10 cursor-pointer'
              } ${busy && !active ? 'opacity-40 cursor-wait' : ''}`}
              data-testid={`training-program-option-${opt.value}`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full shrink-0 ${
                  active ? 'bg-emerald-400 ring-2 ring-emerald-400/30' : 'bg-white/10'
                }`} />
                <span className={`text-sm font-semibold ${active ? 'text-emerald-300' : 'text-white'}`}>
                  {opt.label}
                </span>
                {active && (
                  <span className="text-[10px] uppercase font-bold text-emerald-300 ml-auto">Active</span>
                )}
              </div>
              <p className="text-xs text-white/50 mt-1 ml-5">{opt.desc}</p>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={toggleHistory}
        className="mt-3 w-full flex items-center justify-between text-xs text-white/60 hover:text-white transition-colors py-1"
        data-testid="training-program-history-toggle"
      >
        <span className="inline-flex items-center gap-1.5">
          <History className="w-3.5 h-3.5" />
          Change history
          {history?.length > 0 && <span className="text-white/40 tabular-nums">· {history.length}</span>}
        </span>
        {historyOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {historyOpen && (
        <div className="mt-2 border-t border-white/10 pt-2" data-testid="training-program-history">
          {history === null ? (
            <p className="text-xs text-white/40 italic">Loading…</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-white/40 italic">No changes yet. Any future flips will be recorded here.</p>
          ) : (
            <ul className="space-y-1.5">
              {history.map((h) => (
                <li key={h.id} className="text-xs flex items-baseline gap-2" data-testid={`training-program-history-row-${h.id}`}>
                  <span className="text-white/40 tabular-nums shrink-0 w-32">
                    {new Date(h.at).toLocaleString()}
                  </span>
                  <span className="text-white/70">
                    <span className="text-rose-300">{h.from}</span>
                    {' → '}
                    <span className="text-emerald-300">{h.to}</span>
                  </span>
                  <span className="text-white/40 ml-auto truncate">
                    by {h.who_name || h.who_email}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};


// ── Main Overview tab ────────────────────────────────────────────────────────
export const DomainOverviewTab = ({
  domain, domainId, token, onRefresh, onSendWeeklyEmail, sendingEmail,
  emailRouting, schedule, onAgentClick, onViewAnalytics,
}) => (
  <div className="space-y-4">
    <PipelineHealth domainId={domainId} token={token} />
    <PeakShiftAlert domainId={domainId} token={token} />
    <LeaderboardSection
      domainId={domainId}
      token={token}
      domain={domain}
      onAgentClick={onAgentClick}
      onViewAll={onViewAnalytics}
    />
    <div className="grid md:grid-cols-2 gap-4">
      <CompanyDetails domain={domain} />

      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-3">Monitored Extensions</h3>
        <ExtensionsSection domain={domain} domainId={domainId} token={token} onRefresh={onRefresh} />

        <div className="mt-5">
          <NotificationEmailsSection domain={domain} domainId={domainId} token={token} onRefresh={onRefresh} />
        </div>

        <EmailRoutingSection routing={emailRouting} domainId={domainId} token={token} onRefresh={onRefresh} />

        {emailRouting?.routing?.length > 0 && (
          <SendCoachingButton onSend={onSendWeeklyEmail} sending={sendingEmail} testId="send-coaching-email-overview-btn" />
        )}

        {(!emailRouting || !emailRouting.routing?.length) && domain?.notification_emails?.length > 0 && (
          <SendCoachingButton onSend={onSendWeeklyEmail} sending={sendingEmail} testId="send-coaching-email-overview-btn" />
        )}

        <ScheduleSection domainId={domainId} token={token} initialSchedule={schedule} />
      </div>
    </div>
    <div className="grid md:grid-cols-2 gap-4">
      <RegionSection domain={domain} domainId={domainId} token={token} onRefresh={onRefresh} />
      <TrainingProgramSection domain={domain} domainId={domainId} token={token} onRefresh={onRefresh} />
    </div>
    <BrandingSection domain={domain} domainId={domainId} token={token} onRefresh={onRefresh} />
  </div>
);
